/* ContactQuickActions.jsx — inline Call · Email · WhatsApp triplet.
 *
 * Three small ghost icon links. Pure composition of existing
 * primitives: Lucide icons + the same `h-7 w-7 rounded-lg text-slate-400
 * hover:text-violet-700 hover:bg-violet-50` button grammar already
 * used by `IconBtnSm` in the Deal Room participant rail.
 *
 * Behaviour:
 *   • Each action becomes an `<a>` with the appropriate deep-link
 *     scheme (tel: / mailto: / https://wa.me/…).
 *   • When the underlying value is missing or unrecoverable, the
 *     action is rendered disabled (no link, muted icon, no hover).
 *   • Mobile tap targets default to 40×40 (`density="mobile"`); the
 *     desktop variant uses the compact 28×28 grammar that matches the
 *     existing operational rail density.
 *
 * No layout changes. No new design system. Just deep-links wrapped in
 * the buttons we already use elsewhere.
 */
import React from "react";
import { Phone, Mail, MessageCircle } from "lucide-react";
import { telHref, mailtoHref, whatsappHref } from "../../lib/contactLinks";

const SIZES = {
  // Desktop / operational-rail density (matches IconBtnSm in Deal Room).
  desktop: { btn: "h-7 w-7",  icon: 11.5 },
  // Mobile thumb-reach density per UX spec (40px minimum).
  mobile:  { btn: "h-10 w-10", icon: 15 },
};

/** Single inline action — either an `<a>` deep-link or a disabled button stub. */
function ActionLink({ href, label, Icon, size, testid, onActivate }) {
  const disabled = !href;
  const cls = `${size.btn} rounded-lg inline-flex items-center justify-center transition-colors shrink-0 ${
    disabled
      ? "text-slate-300 cursor-not-allowed"
      : "text-slate-400 hover:text-violet-700 hover:bg-violet-50 active:bg-violet-100"
  }`;
  if (disabled) {
    return (
      <span aria-disabled aria-label={`${label} unavailable`} className={cls} data-testid={`${testid}-disabled`}>
        <Icon size={size.icon} strokeWidth={2.2} />
      </span>
    );
  }
  return (
    <a
      href={href}
      onClick={onActivate}
      // tel: and mailto: don't benefit from a new tab; wa.me does.
      target={href.startsWith("https://wa.me/") ? "_blank" : undefined}
      rel={href.startsWith("https://wa.me/") ? "noreferrer" : undefined}
      aria-label={label}
      className={cls}
      data-testid={testid}
    >
      <Icon size={size.icon} strokeWidth={2.2} />
    </a>
  );
}

/**
 * Inline Call · Email · WhatsApp triplet.
 *
 * @param {object}  p
 * @param {string}  p.phone           — free-text phone (will be sanitised).
 * @param {string}  p.email           — email address.
 * @param {string}  [p.whatsappText]  — optional message to prefill in WhatsApp.
 * @param {"desktop"|"mobile"} [p.density]
 * @param {string}  [p.testidPrefix]  — prefix for the 3 action testids.
 */
export function ContactQuickActions({
  phone,
  email,
  whatsappText,
  density = "desktop",
  testidPrefix = "contact",
  className = "",
}) {
  const size = SIZES[density] || SIZES.desktop;
  const tel  = telHref(phone);
  const mail = mailtoHref(email);
  const wa   = whatsappHref(phone, whatsappText);

  // If nothing is reachable, render nothing — calmer than a row of
  // three dimmed icons screaming "no contact".
  if (!tel && !mail && !wa) return null;

  return (
    <div className={`flex items-center gap-0.5 ${className}`} data-testid={`${testidPrefix}-quick-actions`}>
      <ActionLink href={tel}  label="Call"     Icon={Phone}          size={size} testid={`${testidPrefix}-call`} />
      <ActionLink href={mail} label="Email"    Icon={Mail}           size={size} testid={`${testidPrefix}-email`} />
      <ActionLink href={wa}   label="WhatsApp" Icon={MessageCircle}  size={size} testid={`${testidPrefix}-whatsapp`} />
    </div>
  );
}

export default ContactQuickActions;
